import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { useToast } from '@/shared/contexts/ToastContext'

import {
  askKnowledgeQuestion,
  getKnowledgeConversation,
  getKnowledgeConversations,
  startKnowledgeConversation,
} from '../apis/knowledge'
import {
  type KnowledgeAskError,
  type KnowledgeConversationDetail,
} from '../types/knowledge'

// back が失敗を返した (質問は記録済み・回答は作られていない)。
const ANSWER_ERROR = '回答の生成に失敗しました'
// 応答そのものが届かなかった。**「失敗した」とは書かない** — ALB の idleTimeout (60 秒) で
// 切れた場合、back 側は生成を完走して回答を保存していることがあり、断定すると事実と
// 食い違う。新規会話では会話 id が応答に乗ってこないため画面を移せず、辿る先を示す。
const NO_RESPONSE_ERROR =
  '応答が届きませんでした。回答は生成済みの可能性があります。左の履歴を確認してください'

/**
 * 生成に失敗した応答から会話 id を取り出す。
 *
 * back は失敗しても質問を記録するため、会話は残っている。id を拾って画面をそこへ
 * 向けないと、利用者からは質問が消えたのにサイドバーの履歴にだけ残る。
 */
export const failedConversationId = (
  error: AxiosError<KnowledgeAskError>
): string | undefined => error.response?.data?.conversation_id

/**
 * back が「生成に失敗した」と返したか。
 *
 * 応答が届かなかった場合と区別する。ALB の 504 も通信断も JSON の本体を持たないため、
 * back が返したものかどうかはこの形で見分ける。後者を失敗と書けない理由は
 * `NO_RESPONSE_ERROR` の注記のとおり。
 */
const isAnswerFailure = (error: AxiosError<KnowledgeAskError>): boolean =>
  Boolean(error.response?.data?.error)

// サイドバーの会話履歴。呼び出し側 (KnowledgePane) は IconBar が knowledge を選んでいる間しか
// マウントされないため、取得の可否はマウントが決める。ここに gate を重ねない。
export const useKnowledgeConversationsQuery = () =>
  useQuery({
    queryKey: queryKeys.knowledge.conversations(),
    queryFn: getKnowledgeConversations,
    staleTime: 1000 * 60,
  })

export const useKnowledgeConversationQuery = (
  conversationId: string | undefined
) =>
  useQuery({
    queryKey: queryKeys.knowledge.conversation(conversationId ?? ''),
    queryFn: () => getKnowledgeConversation(conversationId as string),
    enabled: Boolean(conversationId),
  })

export const useStartKnowledgeConversationMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: startKnowledgeConversation,
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.knowledge.conversations(),
      })
      // 返ってきた往復をそのまま初期キャッシュに置く。置かないと遷移直後の詳細が
      // 空になり、質問も回答も消えた空状態が再取得まで一瞬見える。
      queryClient.setQueryData<KnowledgeConversationDetail>(
        queryKeys.knowledge.conversation(result.conversation_id),
        (previous) =>
          previous ?? {
            id: result.conversation_id,
            title: result.messages[0]?.content ?? '',
            created_at: result.messages[0]?.created_at ?? '',
            updated_at:
              result.messages[result.messages.length - 1]?.created_at ?? '',
            messages: result.messages,
          }
      )
    },
    // 失敗しても質問は記録されている。取り直して、失敗した往復を画面に残す。
    // **promise を返して再取得の完了まで待たせる** — 呼び出し側は onSettled で仮の質問を
    // 片付けるので、待たないと取り直しが届くまで質問が画面から消える。
    onError: (error: AxiosError<KnowledgeAskError>) => {
      showToast(
        isAnswerFailure(error) ? ANSWER_ERROR : NO_RESPONSE_ERROR,
        'error'
      )
      const conversationId = failedConversationId(error)
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.knowledge.conversations(),
        }),
        conversationId
          ? queryClient.invalidateQueries({
              queryKey: queryKeys.knowledge.conversation(conversationId),
            })
          : Promise.resolve(),
      ])
    },
  })
}

export const useAskKnowledgeQuestionMutation = (conversationId: string) => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  // **promise を返して再取得の完了まで待たせる** — 呼び出し側は onSettled で仮の質問を
  // 片付けるので、待たないと取り直しが届くまで質問が画面から消える。
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.knowledge.conversation(conversationId),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.knowledge.conversations(),
      }),
    ])

  return useMutation({
    mutationFn: (question: string) =>
      askKnowledgeQuestion(conversationId, question),
    onSuccess: refresh,
    // 失敗しても質問は記録されている。取り直さないと画面から質問だけが消える。
    // 会話は既に開いているので、取り直せば回答が間に合った往復はそのまま出る。
    onError: (error: AxiosError<KnowledgeAskError>) => {
      showToast(
        isAnswerFailure(error) ? ANSWER_ERROR : NO_RESPONSE_ERROR,
        'error'
      )
      return refresh()
    },
  })
}
